import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import api from '../services/api';

const ABIERTOS = ['Pendiente', 'Asignado', 'En Progreso', 'Esperando Usuario'];
const COLOR_ESTADO = {
    Pendiente: 'bg-gray-100 text-gray-700', Asignado: 'bg-blue-100 text-blue-700', 'En Progreso': 'bg-amber-100 text-amber-700',
    'Esperando Usuario': 'bg-purple-100 text-purple-700', Resuelto: 'bg-green-100 text-green-700', Cerrado: 'bg-gray-200 text-gray-600',
    Cancelado: 'bg-red-100 text-red-700',
};
const COLOR_PRIO = { Crítica: 'text-red-600', Alta: 'text-orange-600', Media: 'text-yellow-600', Baja: 'text-green-600' };
const fecha = (d) => (d ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export default function TicketsAgentePanel({ agenteId }) {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState(null);
    const [error, setError] = useState(false);
    const [filtro, setFiltro] = useState('abiertos');
    const [abierto, setAbierto] = useState(null);

    useEffect(() => {
        let vivo = true;
        api.get(`/reportes/agente/${agenteId}/tickets`)
            .then((r) => { if (vivo) setTickets(r.data || []); })
            .catch(() => { if (vivo) setError(true); });
        return () => { vivo = false; };
    }, [agenteId]);

    const visibles = useMemo(() => (tickets || []).filter((t) => (filtro === 'todos' ? true : filtro === 'abiertos' ? ABIERTOS.includes(t.estado) : !ABIERTOS.includes(t.estado))), [tickets, filtro]);
    const nAbiertos = (tickets || []).filter((t) => ABIERTOS.includes(t.estado)).length;

    if (error) return <p className="text-sm text-red-500 py-3">No se pudieron cargar los tickets.</p>;
    if (!tickets) return <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" /></div>;

    return (
        <div className="py-3">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                {[['abiertos', `Abiertos (${nAbiertos})`], ['cerrados', `Resueltos y cerrados (${tickets.length - nAbiertos})`], ['todos', `Todos (${tickets.length})`]].map(([id, t]) => (
                    <button key={id} type="button" onClick={() => setFiltro(id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${filtro === id ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                        {t}
                    </button>
                ))}
            </div>
            {visibles.length === 0 ? <p className="text-sm text-gray-400 py-3">Sin tickets en esta vista.</p> : (
                <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                    {visibles.map((t) => (
                        <div key={t.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                            <button type="button" onClick={() => setAbierto(abierto === t.id ? null : t.id)} className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left">
                                <span className="font-mono text-xs text-gray-500">#{t.id}</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white flex-1 min-w-[10rem] truncate">{t.asunto}</span>
                                <span className="text-xs text-gray-500 truncate">{t.solicitante}</span>
                                <span className={`text-xs font-semibold ${COLOR_PRIO[t.prioridad] || 'text-gray-500'}`}>{t.prioridad}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${COLOR_ESTADO[t.estado] || 'bg-gray-100 text-gray-600'}`}>{t.estado}</span>
                                <span className="text-xs text-gray-400">{fecha(t.fechaCreacion)}</span>
                            </button>
                            {abierto === t.id && (
                                <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700 text-sm">
                                    <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line mb-2">{t.descripcion || 'Sin descripción'}</p>
                                    <dl className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                                        {[['Empresa', t.empresa], ['Creado', fecha(t.fechaCreacion)], ['1ra respuesta', fecha(t.fechaPrimeraRespuesta)], ['Resuelto', fecha(t.fechaResolucion)], ['Cerrado', fecha(t.fechaCierre)]].map(([k, v]) => (
                                            <div key={k}><dt className="text-gray-400">{k}</dt><dd className="text-gray-800 dark:text-gray-200">{v || '—'}</dd></div>
                                        ))}
                                    </dl>
                                    <button type="button" onClick={() => navigate(`/tickets/${t.id}`)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
                                        Abrir ticket <ExternalLink size={12} />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
